import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import AuthShell from '../Layout/AuthShell';

interface ResetPasswordData {
  newPassword: string;
  confirmPassword: string;
}

interface ResetPasswordResponse {
  success: boolean;
  data: unknown;
  expandedError?: {
    errorCode: number;
    message: string;
    technicalMessage: string | null;
  };
}

const PASSWORD_REQUIREMENTS = [
  {
    label: 'Minimum 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    label: 'At least one uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    label: 'At least one lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    label: 'At least one digit',
    test: (password: string) => /\d/.test(password),
  },
  {
    label: 'At least one special character',
    test: (password: string) => /[@$!%*?&._]/.test(password),
  },
];

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ResetPasswordData>({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [linkChecked, setLinkChecked] = useState(false);
  const newPasswordInputRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const emailParam = searchParams.get('Email');
    const tokenParam = searchParams.get('token');

    if (!emailParam || !tokenParam) {
      setError('Invalid reset password link. Please request a new password reset.');
      setLinkChecked(true);
      return;
    }

    setEmail(emailParam);
    setToken(tokenParam.replace(/ /g, '+'));
    setLinkChecked(true);
  }, [searchParams]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const redirectHandle = window.setTimeout(() => {
      navigate('/login', {
        replace: true,
        state: {
          message: 'Password reset successfully. Sign in with your new password.',
        },
      });
    }, 1800);

    return () => {
      window.clearTimeout(redirectHandle);
    };
  }, [navigate, success]);

  const readFormDataFromInputs = useCallback((): ResetPasswordData => ({
    newPassword: newPasswordInputRef.current?.value ?? formData.newPassword,
    confirmPassword: confirmPasswordInputRef.current?.value ?? formData.confirmPassword,
  }), [formData.confirmPassword, formData.newPassword]);

  const syncFormDataFromInputs = useCallback(() => {
    const nextFormData = readFormDataFromInputs();
    setFormData((current) => (
      current.newPassword === nextFormData.newPassword &&
      current.confirmPassword === nextFormData.confirmPassword
        ? current
        : nextFormData
    ));
  }, [readFormDataFromInputs]);

  const validatePassword = (password: string): string[] => {
    return PASSWORD_REQUIREMENTS
      .filter((requirement) => !requirement.test(password))
      .map((requirement) => requirement.label);
  };

  useEffect(() => {
    if (!linkChecked || !email || !token) {
      return undefined;
    }

    const sync = () => {
      const nextNewPassword = newPasswordInputRef.current?.value ?? '';
      const nextConfirmPassword = confirmPasswordInputRef.current?.value ?? '';

      setFormData((current) => (
        current.newPassword === nextNewPassword &&
        current.confirmPassword === nextConfirmPassword
          ? current
          : {
            newPassword: nextNewPassword,
            confirmPassword: nextConfirmPassword,
          }
      ));
    };

    sync();

    const timers = [0, 250, 1000].map((delay) => window.setTimeout(sync, delay));
    window.addEventListener('focus', sync);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('focus', sync);
    };
  }, [email, linkChecked, token]);

  const handleInputChange = (field: keyof ResetPasswordData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const nextFormData = readFormDataFromInputs();
      setFormData(nextFormData);

      if (!nextFormData.newPassword) {
        setError('Please enter a new password');
        return;
      }

      if (!nextFormData.confirmPassword) {
        setError('Please confirm your new password');
        return;
      }

      if (nextFormData.newPassword !== nextFormData.confirmPassword) {
        setError('New password and confirm password do not match');
        return;
      }

      const passwordErrors = validatePassword(nextFormData.newPassword);
      if (passwordErrors.length > 0) {
        setError(`Password does not meet the required criteria: ${passwordErrors.join(', ')}`);
        return;
      }

      if (!email || !token) {
        setError('Invalid reset password link. Please request a new password reset.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          token,
          newPassword: nextFormData.newPassword,
        }),
      });

      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0' || !response.body) {
          setSuccess('Password reset successfully. Redirecting you back to login...');
          setFormData({ newPassword: '', confirmPassword: '' });
        } else {
          try {
            const data: ResetPasswordResponse = await response.json();
            if (data.success) {
              setSuccess('Password reset successfully. Redirecting you back to login...');
              setFormData({ newPassword: '', confirmPassword: '' });
            } else {
              setError(data.expandedError?.message || 'Failed to reset password');
            }
          } catch {
            setSuccess('Password reset successfully. Redirecting you back to login...');
            setFormData({ newPassword: '', confirmPassword: '' });
          }
        }
      } else {
        try {
          const data: ResetPasswordResponse = await response.json();
          setError(data.expandedError?.message || 'Failed to reset password');
        } catch {
          setError(`Failed to reset password (${response.status})`);
        }
      }
    } catch (submitError) {
      console.error('Error resetting password:', submitError);
      setError('An error occurred while resetting password');
    } finally {
      setLoading(false);
    }
  };

  const resolvedFormData = useMemo(readFormDataFromInputs, [readFormDataFromInputs]);
  const passwordErrors = resolvedFormData.newPassword ? validatePassword(resolvedFormData.newPassword) : [];
  const passwordsMatch = resolvedFormData.confirmPassword.length > 0 && resolvedFormData.newPassword === resolvedFormData.confirmPassword;
  const invalidLink = linkChecked && (!email || !token);

  return (
    <AuthShell
      title="Choose a new password"
      description="Use a strong password for the operator workspace. Once saved, your old password stops working immediately."
      footer={(
        <div className="auth-link-row">
          <Link to={invalidLink ? '/forgot-password' : '/login'} className="auth-link">
            {invalidLink ? 'Request a new reset link' : 'Back to login'}
          </Link>
        </div>
      )}
    >
      {!linkChecked ? null : invalidLink ? (
        <Alert variant="danger">
          {error || 'Invalid reset password link. Please request a new password reset.'}
        </Alert>
      ) : (
        <>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          {success ? <Alert variant="success">{success}</Alert> : null}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup className="auth-input-group">
                <Form.Control
                  ref={newPasswordInputRef}
                  className="auth-control"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  onInput={syncFormDataFromInputs}
                  onBlur={syncFormDataFromInputs}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="auth-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </Button>
              </InputGroup>

              {formData.newPassword ? (
                <ul className="auth-checklist">
                  <li className={passwordErrors.includes('Minimum 8 characters') ? 'text-danger' : 'text-success'}>
                    Minimum 8 characters
                  </li>
                  <li className={passwordErrors.includes('At least one uppercase letter') ? 'text-danger' : 'text-success'}>
                    At least one uppercase letter
                  </li>
                  <li className={passwordErrors.includes('At least one lowercase letter') ? 'text-danger' : 'text-success'}>
                    At least one lowercase letter
                  </li>
                  <li className={passwordErrors.includes('At least one digit') ? 'text-danger' : 'text-success'}>
                    At least one digit
                  </li>
                  <li className={passwordErrors.includes('At least one special character') ? 'text-danger' : 'text-success'}>
                    At least one special character
                  </li>
                </ul>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <InputGroup className="auth-input-group">
                <Form.Control
                  ref={confirmPasswordInputRef}
                  className="auth-control"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  onInput={syncFormDataFromInputs}
                  onBlur={syncFormDataFromInputs}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
              </InputGroup>
              {resolvedFormData.confirmPassword && !passwordsMatch ? (
                <Form.Text className="text-danger">
                  Passwords do not match.
                </Form.Text>
              ) : null}
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={loading || invalidLink}
                className="auth-primary-btn mt-3"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </Form>
        </>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
